import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import "./NotificationsTab.css";
import { toast } from "react-toastify";
import NavbarIconButton from "../buttons/NavbarIconButton";
import { onMessageListener } from "../firebase/firebase";

const NotificationsTab = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [lastViewedTime, setLastViewedTime] = useState(Date.now());
  const API_URL = import.meta.env.VITE_API_URL;
  const accessToken = useSelector((state) => state.user.accessToken);
  const userId = useSelector((state) => state.user.user.id);
  const dropdownRef = useRef(null);

  const fetchNotifications = () => {
    setLoading(true);
    axios
      .get(`${API_URL}Notifications/get-notifications`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { userId: userId }
      })
      .then((res) => {
        setNotifications(res.data.notifications);
        setLoading(false);

        // Check if there are any unread notifications
        const hasUnread = res.data.notifications.some(notification =>
          notification.unread || new Date(notification.createdAt) > lastViewedTime
        );

        setHasNewNotifications(hasUnread);
      })
      .catch((error) => {
        console.error("Error fetching notifications:", error);
        setLoading(false);
      });
  };

  // Listen for new notifications from Firebase
  useEffect(() => {
    const messageHandler = (payload) => {
      // Create a new notification object from the payload
      const newNotification = {
        id: Date.now(), // Generate a temporary ID
        title: payload.notification?.title || "New Notification",
        body: payload.notification?.body || "You have a new notification",
        createdAt: new Date().toISOString(),
        unread: true
      };

      // Add the new notification to the state
      setNotifications(prevNotifications => [newNotification, ...prevNotifications]);

      // Set new notifications flag to true to show the red dot
      setHasNewNotifications(true);

      // Show a toast notification
      toast.info(newNotification.title, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    };

    onMessageListener()
      .then(messageHandler)
      .catch((err) => console.error("Error in Firebase message listener:", err));

    const intervalId = setInterval(() => {
      onMessageListener()
        .then(messageHandler)
        .catch((err) => console.error("Error refreshing message listener:", err));
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // For testing - uncomment to simulate a notification
  useEffect(() => {
    // Set hasNewNotifications to true for testing
    setHasNewNotifications(true);
  }, []);

  // Initial fetch and outside click handler
  useEffect(() => {
    // Initial fetch of notifications
    fetchNotifications();

    // Close the dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    // Add event listener for ESC key to close the dropdown
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscKey);

    // Cleanup function
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [open]);

  // Handle opening the dropdown
  const handleOpenDropdown = () => {
    setOpen(!open);
    if (!open) {
      fetchNotifications();
      setLastViewedTime(Date.now());
      setHasNewNotifications(false);
    }
  };

  return (
    <div className="notifications-container">
      <div
        className="bell-button"
        onClick={handleOpenDropdown}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleOpenDropdown();
          }
        }}
        style={{ position: 'relative' }}
        aria-label="Notifications"
        aria-expanded={open}
      >
        <NavbarIconButton iconPath="/assets/images/notification-icon.svg" />
        {hasNewNotifications && (
          <span
            className="notification-badge"
            aria-label="New notifications"
          ></span>
        )}
      </div>

      {open && (
        <div className="dropdown" ref={dropdownRef} role="dialog" aria-label="Notifications panel">
          <div className="dropdown-header">
            <h4>Notifications</h4>
            <button
              className="close-mobile-button"
              onClick={() => setOpen(false)}
              aria-label="Close notifications panel"
            >
              &times;
            </button>
          </div>

          <div className="dropdown-content">
            {loading ? (
              <div className="loading-indicator">
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  className={`dropdown-item ${notification.unread ? 'unread' : ''}`}
                  key={notification.id}
                >
                  <div className="notification-header">
                    <strong>{notification.title || "New Notification"}</strong>
                    <small>
                      {new Date(notification.createdAt).toLocaleDateString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </small>
                  </div>
                  <p className="notification-body">{notification.body}</p>
                </div>
              ))
            ) : (
              <div className="dropdown-item empty-state">
                <p>No notifications available</p>
              </div>
            )}
          </div>
          {/* <div className="dropdown-footer">
            <button
              className="clear-all-button"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
